import { useAuthStore } from "@/stores/auth";

import { API_BASE_URL } from "@/api/url";
import { isInvalidSessionResponse } from "@/composables/authSession";
import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";

type RetriableConfig = InternalAxiosRequestConfig & {
  _sessionRetried?: boolean;
};

let client: AxiosInstance | null = null;
export const useClient = () => {
  const authStore = useAuthStore();
  if (!client) {
    const instance = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        Authorization: `Bearer ${authStore.getAccessToken}`,
        "Content-Type": "application/json",
      },
    });
    client = instance;

    const refreshOrExpire = async () => {
      try {
        await authStore.refresh();
      } catch (error) {
        // Rejected refreshes clear the session; transient failures leave it intact.
        if (!authStore.isAuthenticated()) await authStore.expireSession();
        throw error;
      }
    };

    //create interceptor to automatically refresh the access token
    instance.interceptors.request.use(
      async (config) => {
        if (authStore.refreshRequired() && authStore.isAuthenticated()) {
          await refreshOrExpire();
        }

        // if we have a valid escalationToken, use it
        if (!authStore.escalationTokenExpired() && authStore.escalationToken) {
          config.headers.Authorization = `Bearer ${authStore.escalationToken.token}`;
        } else {
          config.headers.Authorization = `Bearer ${authStore.getAccessToken}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Compass rejected the session token: renew once and replay, else go to login.
    instance.interceptors.response.use(undefined, async (error: AxiosError) => {
      const config = error.config as RetriableConfig | undefined;
      if (
        !config ||
        config._sessionRetried ||
        !isInvalidSessionResponse(error.response?.status, error.response?.data)
      )
        throw error;
      config._sessionRetried = true;
      const sentWith = String(config.headers.Authorization ?? "");
      if (
        authStore.escalationToken &&
        sentWith === `Bearer ${authStore.escalationToken.token}`
      ) {
        authStore.deescalate();
        return instance.request(config);
      }
      // A concurrent request may already have renewed the token.
      if (
        authStore.accessToken &&
        sentWith !== `Bearer ${authStore.accessToken.token}`
      )
        return instance.request(config);
      if (!authStore.isAuthenticated()) {
        await authStore.expireSession();
        throw error;
      }
      await refreshOrExpire();
      return instance.request(config);
    });
  }

  return client;
};
